import { z } from "zod";

import {
  SCHEMA_LIMITS,
  createMaxLengthMessage,
  ALLOWED_DURATIONS,
  ALLOWED_STEP_VALUES,
  isAllowedDuration,
  isAllowedStepValue,
} from "./constants";

// Shared field definitions for reuse across session schemas
const sessionTitleOptionalSchema = z
  .string()
  .max(
    SCHEMA_LIMITS.SESSION_TITLE_MAX_LENGTH,
    createMaxLengthMessage(
      "Session title",
      SCHEMA_LIMITS.SESSION_TITLE_MAX_LENGTH,
    ),
  )
  .optional();

/**
 * Session meeting link — now OPTIONAL. Coaches often schedule the actual meeting
 * (Zoom/Meet/Teams) after creating the program, so a session may have no link
 * yet. Accepts a valid URL, `null`, or `undefined`.
 *
 * Update semantics ({@link UpdateSessionSchema}): the edit form always resubmits
 * the full current value, so an explicit `null` here CLEARS a previously-stored
 * link (a full replace). `undefined` (field omitted) leaves the stored link
 * untouched — Drizzle skips `undefined` keys in `.set()`.
 */
const sessionMeetingUrlSchema = z
  .string()
  .url("Please enter a valid URL")
  .nullish();

/**
 * Form-field variant of the meeting link: an empty string is allowed and
 * represents "no link" (the submit handler maps it to `null`).
 */
const sessionMeetingUrlFormSchema = z
  .string()
  .url("Please enter a valid URL")
  .or(z.literal(""))
  .optional();

export const MonthlySliceSchema = z
  .object({
    year: z.number().int().min(1970).max(2100),
    month: z.number().int().min(1).max(12),
  })
  .default({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

export const DailySliceSchema = z
  .object({
    startTimestamp: z.string().datetime(),
    endTimestamp: z.string().datetime(),
  })
  .default({
    startTimestamp: "2025-01-05T00:00:00Z",
    endTimestamp: "2025-02-06T10:00:00Z",
  });

/** Guard against unbounded range queries (a whole-year view spans ~366 days). */
export const MAX_SESSION_RANGE_DAYS = 400;

/**
 * Range query for {@link getCoachSessionsInRangeFn}. Unlike the daily slice this
 * can cover many months, so cap the span to keep a single request bounded.
 */
export const SessionsInRangeSchema = z
  .object({
    startTimestamp: z.string().datetime(),
    endTimestamp: z.string().datetime(),
  })
  .refine(
    ({ startTimestamp, endTimestamp }) =>
      new Date(endTimestamp).getTime() - new Date(startTimestamp).getTime() <=
      MAX_SESSION_RANGE_DAYS * 24 * 60 * 60 * 1000,
    {
      message: `Date range is too large — request at most ${MAX_SESSION_RANGE_DAYS} days at a time.`,
    },
  )
  .default({
    startTimestamp: "2025-01-05T00:00:00Z",
    endTimestamp: "2025-02-06T10:00:00Z",
  });

export const UpdateSessionSchema = z.object({
  id: z.string().uuid(),
  title: sessionTitleOptionalSchema,
  engagementId: z.string().uuid(),
  status: z
    .enum(["scheduled", "completed", "cancelled", "no_show"])
    .default("scheduled"),
  startAt: z.string().datetime(),
  duration: z.number().int().min(1).optional(),
  meetingUrl: sessionMeetingUrlSchema,
  keySummary: z.string().optional(),
  coachTimeZone: z.string(),
});

export const CreateSessionSchema = z.object({
  engagementId: z.string().uuid(),
  status: z
    .enum(["scheduled", "completed", "cancelled", "no_show"])
    .default("scheduled"),
  startAt: z.string().datetime(),
  title: sessionTitleOptionalSchema,
  duration: z.number().int().min(1),
  meetingUrl: sessionMeetingUrlSchema,
  keySummary: z.string().optional(),
  coachTimeZone: z.string(),
});

export const DeleteSessionSchema = z.object({
  id: z.string().uuid(),
  coachTimeZone: z.string(),
});

export const DailyDurationSliceSchema = z.object({
  startTimestamp: z.string().datetime(),
  duration: z
    .number()
    .int()
    .refine(isAllowedDuration, {
      message: `duration must be one of ${ALLOWED_DURATIONS.join(", ")}`,
    }),
  step: z
    .number()
    .int()
    .refine(isAllowedStepValue, {
      message: `step must be one of ${ALLOWED_STEP_VALUES.join(", ")}`,
    })
    .optional(),
  coachTimeZone: z.string(),
  /** Optional per-session title; falls back to a computed default at display time. */
  title: sessionTitleOptionalSchema,
  /**
   * Optional per-session meeting link. When omitted, the engagement's default
   * `meeting_link` (if any) is used at insert time.
   */
  meeting_url: z.string().url("Please enter a valid URL").optional(),
});

export const ArrayOfDailyDurationSchema = z
  .array(DailyDurationSliceSchema)
  .nonempty();

export const UpdateSessionDurationSchema = z.object({
  engagementId: z.string().uuid(),
});

export const SessionHistorySchema = z.object({
  engagementId: z.string().uuid(),
  limit: z.number(),
  page: z.number(),
});

export const SessionSummarySchema = z.object({
  sessionId: z.string().uuid(),
});

export const AllEngagementSessions = z.object({
  engagementId: z.string().uuid(),
});

export const SessionEditFormSchema = z.object({
  // Optional: an empty title is valid and falls back to the computed default
  // (`Session {n}`) shown as the field's placeholder.
  title: sessionTitleOptionalSchema,
  date: z.date(),
  time: z.string().min(1, "Time is required"),
  duration: z.number().int().min(1).optional(),
  subject: z.string().optional(),
  meetingUrl: sessionMeetingUrlFormSchema,
});

export type SessionEditFormData = z.infer<typeof SessionEditFormSchema>;

export type MonthlySliceInput = z.infer<typeof MonthlySliceSchema>;
export type DailySliceInput = z.infer<typeof DailySliceSchema>;
export type UpdateSessionInput = z.infer<typeof UpdateSessionSchema>;
export type CreateSessionInput = z.infer<typeof CreateSessionSchema>;
export type DeleteSessionInput = z.infer<typeof DeleteSessionSchema>;
export type SessionHistorySchemaInput = z.infer<typeof SessionHistorySchema>;
export type DailyDurationSliceInput = z.infer<typeof DailyDurationSliceSchema>;
export type IntervalMinutesInput = z.infer<typeof ArrayOfDailyDurationSchema>;
